import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Grade1Pinyin.css";

/** Helper to shuffle arrays */
function shuffleArray(arr) {
  const cloned = [...arr];
  for (let i = cloned.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
}

/** 
 * Basic Pinyin Table snippet
 */
const pinyinTable = (
  <div className="pinyin-table-wrapper">
    <h4>Basic Pinyin Table</h4>
    <table className="pinyin-table">
      <thead>
        <tr>
          <th>Initial</th>
          <th>Approx. Sound</th>
          <th>Example</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>b</td>
          <td>unaspirated “b” (like “sp” in “spa”)</td>
          <td>bā (八) = “eight”</td>
        </tr>
        <tr>
          <td>p</td>
          <td>aspirated “p” (like “p” in “pot”)</td>
          <td>pà (怕) = “fear”</td>
        </tr>
        <tr>
          <td>m</td>
          <td>“m” as in English “mother”</td>
          <td>mā (妈) = “mom”</td>
        </tr>
        <tr>
          <td>f</td>
          <td>“f” as in English “fun”</td>
          <td>fā (发) = “hair” or “to send”</td>
        </tr>
      </tbody>
    </table>
    <table className="pinyin-table" style={{ marginTop: "1em" }}>
      <thead>
        <tr>
          <th>Final</th>
          <th>Approx. Sound</th>
          <th>Example</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>-a</td>
          <td>“ah” like “father”</td>
          <td>mā, pá, dà</td>
        </tr>
        <tr>
          <td>-ao</td>
          <td>like “ow” in “cow” but a bit more “ao”</td>
          <td>hǎo (好) = “good”</td>
        </tr>
        <tr>
          <td>-i</td>
          <td>like “ee” in “see” (after most initials)</td>
          <td>mī, xǐ, lì</td>
        </tr>
        <tr>
          <td>-ian</td>
          <td>like “yen” or “ian” with slight “y”</td>
          <td>miǎn, lián</td>
        </tr>
      </tbody>
    </table>
    <p style={{ marginTop: "0.5em" }}>
      <em>Note:</em> Pinyin uses tone marks (ā, á, ǎ, à). Practice each syllable with the correct tone!
    </p>
  </div>
);

/** 25-question pool about Pinyin basics */
const pinyinQuestions = [
  {
    question: "What is Pinyin?",
    correctAnswer: "A Romanized system to represent Mandarin pronunciation",
    options: [
      "A Chinese character system",
      "A Romanized system to represent Mandarin pronunciation",
      "A type of calligraphy"
    ]
  },
  {
    question: "How many tones are in Mandarin Pinyin (excluding the neutral tone)?",
    correctAnswer: "4",
    options: ["3", "4", "5"]
  },
  {
    question: "Which syllable represents the sound for the number eight?",
    correctAnswer: "bā",
    options: ["bā", "pā", "mā"]
  },
  {
    question: "What do the tone marks in Pinyin indicate?",
    correctAnswer: "The pitch contour of the syllable",
    options: [
      "The pitch contour of the syllable",
      "The meaning of the word",
      "They have no meaning"
    ]
  },
  {
    question: "Which tone is represented by a flat line above the vowel?",
    correctAnswer: "1st tone",
    options: ["1st tone", "2nd tone", "3rd tone"]
  },
  {
    question: "The syllable 'mā' means what?",
    correctAnswer: "Mother",
    options: ["Father", "Mother", "Child"]
  },
  {
    question: "Which initial is aspirated in Pinyin?",
    correctAnswer: "p",
    options: ["b", "p", "m"]
  },
  {
    question: "Which syllable uses a falling tone?",
    correctAnswer: "mà",
    options: ["mā", "má", "mà"]
  },
  {
    question: "What is one major purpose of using Pinyin?",
    correctAnswer: "To help with pronunciation of Chinese characters",
    options: [
      "To help with pronunciation of Chinese characters",
      "To learn Chinese grammar",
      "To translate Chinese to English"
    ]
  },
  {
    question: "Pinyin is most widely used in which region?",
    correctAnswer: "Mainland China",
    options: ["Mainland China", "Taiwan", "Japan"]
  }
  // (You can add more questions up to 25 as needed)
];

export default function Grade1Pinyin() {
  // Single topic: Pinyin
  const [quiz, setQuiz] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [finalQuizQuestions, setFinalQuizQuestions] = useState([]);
  const [finalQuizActive, setFinalQuizActive] = useState(false);
  const [finalQuizIndex, setFinalQuizIndex] = useState(0);
  const [finalQuizScore, setFinalQuizScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes

  // Timer for final quiz
  useEffect(() => {
    if (finalQuizActive && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft((prev) => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFinalQuizActive(false);
    }
  }, [finalQuizActive, timeLeft]);

  // Generate a random Quick Quiz question from pinyinQuestions
  const generateQuiz = () => {
    const q = pinyinQuestions[Math.floor(Math.random() * pinyinQuestions.length)];
    setQuiz({
      question: q.question,
      correctAnswer: q.correctAnswer,
      options: shuffleArray([...q.options]),
    });
    setSelectedAnswer(null);
    setFeedback(null);
  };

  // Handle Quick Quiz answer
  const handleAnswerClick = (answer) => {
    setSelectedAnswer(answer);
    if (quiz && answer === quiz.correctAnswer) {
      setFeedback("✅ Correct!");
    } else {
      setFeedback("❌ Try Again!");
    }
    setTimeout(() => {
      generateQuiz();
    }, 1500);
  };

  // Start Final Quiz using the entire pinyinQuestions array
  const startFinalQuiz = () => {
    const finalSet = shuffleArray(pinyinQuestions);
    setFinalQuizQuestions(finalSet);
    setFinalQuizActive(true);
    setFinalQuizIndex(0);
    setFinalQuizScore(0);
    setTimeLeft(300);
  };

  const handleFinalQuizAnswer = (choice) => {
    if (finalQuizQuestions[finalQuizIndex].correctAnswer === choice) {
      setFinalQuizScore((prev) => prev + 1);
    }
    if (finalQuizIndex < finalQuizQuestions.length - 1) {
      setFinalQuizIndex((prev) => prev + 1);
    } else {
      setFinalQuizActive(false);
    }
  };

  return (
    <div className="g1pin-page">
      <h1 className="g1pin-title">Grade 1 Pinyin Lessons</h1>
      <p className="g1pin-description">
        Learn the basics of Pinyin – the Romanized system for Mandarin pronunciation.
      </p>

      {/* Lesson Content */}
      <div className="g1pin-lesson-content">
        <h2 className="g1pin-lesson-title">Pinyin Basics</h2>
        <p className="g1pin-lesson-description">
          Pinyin uses the Roman alphabet along with tone marks to represent Mandarin sounds.
          Study the table below to become familiar with initials, finals, and tones.
        </p>
        {pinyinTable}
      </div>

      {/* Quiz Section */}
      <div className="g1pin-quiz-double-container">
        {/* Quick Quiz */}
        <div className="g1pin-quick-quiz-container">
          <h2 className="g1pin-quiz-title">Quick Quiz!</h2>
          {!quiz && (
            <button className="g1pin-quiz-btn" onClick={generateQuiz}>
              Start Quick Quiz
            </button>
          )}
          {quiz && (
            <>
              <p className="g1pin-quiz-question">{quiz.question}</p>
              <div className="g1pin-quiz-options">
                {quiz.options.map((opt, i) => (
                  <button
                    key={i}
                    className={`g1pin-quiz-btn ${selectedAnswer === opt ? "selected" : ""}`}
                    onClick={() => handleAnswerClick(opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              {feedback && <p className="g1pin-quiz-feedback">{feedback}</p>}
            </>
          )}
        </div>

        {/* Final Quiz */}
        <div className="g1pin-final-quiz-container">
          <h2 className="g1pin-quiz-title">Final Quiz</h2>
          {!finalQuizActive && (
            <button className="g1pin-final-quiz-button" onClick={startFinalQuiz}>
              Start Final Quiz (25 Questions)
            </button>
          )}
          {finalQuizActive && finalQuizQuestions.length > 0 && (
            <div className="g1pin-final-quiz-content">
              <p>
                Time Left: {Math.floor(timeLeft / 60)}:
                {String(timeLeft % 60).padStart(2, "0")}
              </p>
              <p>Question {finalQuizIndex + 1} of {finalQuizQuestions.length}</p>
              <p className="g1pin-quiz-question">
                {finalQuizQuestions[finalQuizIndex].question}
              </p>
              <div className="g1pin-quiz-options">
                {finalQuizQuestions[finalQuizIndex].options.map((choice, idx) => (
                  <button key={idx} onClick={() => handleFinalQuizAnswer(choice)}>
                    {choice}
                  </button>
                ))}
              </div>
              <p>Score: {finalQuizScore}</p>
              {finalQuizIndex === finalQuizQuestions.length - 1 && (
                <p>Final Score: {finalQuizScore}/{finalQuizQuestions.length}</p>
              )}
            </div>
          )}
        </div>
      </div>

      <Link to="/ChineseSubject" className="g1pin-back-button">
        ← Back to Chinese Page
      </Link>
    </div>
  );
}
